import React, { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, MapPin, Plus, Trash2, X, Users, Loader2 } from "lucide-react";
import { listPins, createPin, deletePin, errorMessage } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { toast } from "sonner";

/**
 * Interactive "tap-a-face" memory layer for an archival group photo.
 * - Public visitors see existing pins; click a pin to read the name + memory note.
 * - Admin (sessionStorage.sp_token present) gets an "Add memory" mode:
 *   click anywhere on the photo → fill name/note → save. Pin coordinates
 *   are stored as percentages so they survive any image size.
 *
 * Props:
 *   photoKey: string (stable identifier for this photo, e.g. "school-1966")
 *   src, title, caption, year, category
 */
export default function ClassPhotoMemories({ photoKey, src, title, caption, year, category }) {
  const imgWrapRef = useRef(null);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePin, setActivePin] = useState(null); // existing pin clicked
  const [addMode, setAddMode] = useState(false);
  const [pendingPos, setPendingPos] = useState(null); // {x, y} in %
  const [form, setForm] = useState({ name: "", note: "" });
  const [saving, setSaving] = useState(false);

  const isAdmin = typeof window !== "undefined" && !!sessionStorage.getItem("sp_token");

  // Derive a friendly memory label from the photo's year (e.g. "c. 1966" -> "'66", "1969-70" -> "'69-'70").
  const memoryLabel = (() => {
    const matches = String(year || "").match(/(\d{2,4})/g) || [];
    const shorten = (s) => (s.length === 4 ? `'${s.slice(2)}` : `'${s}`);
    if (matches.length === 0) return "Memories";
    if (matches.length === 1) return `Memories of ${shorten(matches[0])}`;
    return `Memories of ${shorten(matches[0])}-${shorten(matches[1])}`;
  })();

  const refreshPins = useCallback(async () => {
    try {
      const data = await listPins(photoKey);
      setPins(data || []);
    } catch (err) {
      // silent — feature is non-critical
    } finally {
      setLoading(false);
    }
  }, [photoKey]);

  useEffect(() => { refreshPins(); }, [refreshPins]);

  // Click on the photo (only when in add-mode)
  const onPhotoClick = (e) => {
    if (!addMode || !imgWrapRef.current) return;
    const rect = imgWrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPos({ x, y });
    setForm({ name: "", note: "" });
  };

  const cancelAdd = () => {
    setPendingPos(null);
    setForm({ name: "", note: "" });
  };

  const submitPin = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const saved = await createPin({
        photo_key: photoKey,
        x: pendingPos.x,
        y: pendingPos.y,
        name: form.name.trim(),
        note: form.note.trim(),
      });
      setPins((p) => [...p, saved]);
      setPendingPos(null);
      setForm({ name: "", note: "" });
      toast.success("Memory saved");
    } catch (err) {
      toast.error(errorMessage(err, "Could not save memory"));
    } finally {
      setSaving(false);
    }
  };

  const removePin = async (id) => {
    try {
      await deletePin(id);
      setPins((p) => p.filter((x) => x.id !== id));
      setActivePin(null);
      toast.success("Memory removed");
    } catch (err) {
      toast.error(errorMessage(err, "Could not delete memory"));
    }
  };

  return (
    <figure
      className="col-span-12 polaroid relative gold-corner"
      data-testid="class-photo-memories"
    >
      {/* Header strip — interactive cue */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4 px-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 bg-emerald-deep text-champagne text-[10px] uppercase tracking-[0.3em] font-bold px-3 py-1.5 rounded-full">
            <Users className="w-3 h-3" /> {memoryLabel}
          </span>
          <span className="text-stone-600 text-xs italic">
            {pins.length === 0
              ? "No one has been identified yet."
              : `${pins.length} classmate${pins.length === 1 ? "" : "s"} identified — hover the dots`}
          </span>
        </div>
        {isAdmin && (
          <button
            data-testid="memories-toggle-add-btn"
            onClick={() => { setAddMode(!addMode); setPendingPos(null); setActivePin(null); }}
            className={`text-[11px] uppercase tracking-[0.25em] font-bold px-3.5 py-1.5 rounded-full border transition-all ${
              addMode
                ? "bg-saffron text-emerald-deep border-saffron"
                : "bg-emerald-deep text-champagne border-emerald-deep hover:bg-navy"
            }`}
          >
            {addMode ? "Cancel" : (
              <span className="inline-flex items-center gap-1.5"><Plus className="w-3 h-3" /> Add memory</span>
            )}
          </button>
        )}
      </div>

      {/* Photo with overlay */}
      <div
        ref={imgWrapRef}
        onClick={onPhotoClick}
        className={`photo-area aspect-[4/3] relative ${addMode ? "cursor-crosshair ring-4 ring-saffron/60" : ""}`}
        data-testid="memories-photo-canvas"
      >
        <img src={src} alt={title} className="w-full h-full object-cover photo-enhance" />

        {/* Subtle overlay when in add-mode */}
        {addMode && (
          <div className="absolute inset-0 bg-emerald-deep/10 pointer-events-none flex items-start justify-center pt-4">
            <span className="bg-emerald-deep/90 text-champagne text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full backdrop-blur">
              Tap on a face to identify
            </span>
          </div>
        )}

        {/* Existing pins */}
        {pins.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`memory-pin-${p.id}`}
            onClick={(e) => { e.stopPropagation(); setActivePin(p); }}
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
            className="absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-saffron text-emerald-deep border-2 border-emerald-deep shadow-lg hover:scale-125 hover:bg-coral hover:text-white transition-all flex items-center justify-center"
            title={p.name}
          >
            <span className="text-[11px] font-bold">{p.name.split(" ")[0]?.charAt(0) || "•"}</span>
          </button>
        ))}

        {/* Pending pin (admin add-mode mid-add) */}
        {pendingPos && (
          <div
            style={{ left: `${pendingPos.x}%`, top: `${pendingPos.y}%` }}
            className="absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-coral text-white border-2 border-white shadow-xl animate-pulse flex items-center justify-center"
          >
            <MapPin className="w-4 h-4" />
          </div>
        )}

        {loading && (
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-emerald-deep/70 flex items-center justify-center backdrop-blur">
            <Loader2 className="w-4 h-4 text-champagne animate-spin" />
          </div>
        )}
      </div>

      {/* Caption block */}
      <figcaption className="pt-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 bg-emerald-soft text-emerald-deep text-[10px] uppercase tracking-[0.25em] font-bold px-2.5 py-1 rounded-full">
            <Sparkles className="w-3 h-3" /> {category}
          </span>
          <span className="ml-auto text-stone-500 text-[11px] tracking-tight italic">{year}</span>
        </div>
        <div className="font-display text-navy text-xl mt-3 leading-tight">{title}</div>
        <div className="font-serif-accent italic text-stone-600 text-sm mt-1">{caption}</div>
        {pins.length > 0 && (
          <div className="mt-4 pt-4 border-t border-stone-200/70 flex flex-wrap gap-2">
            {pins.map((p) => (
              <button
                key={`chip-${p.id}`}
                onClick={() => setActivePin(p)}
                data-testid={`memory-chip-${p.id}`}
                className="text-[11px] uppercase tracking-[0.2em] font-medium px-3 py-1.5 rounded-full bg-cream border border-saffron/40 text-stone-700 hover:bg-saffron/30 transition-colors"
              >
                {p.name}
              </button>
            ))}
          </div>
        )}
      </figcaption>

      {/* Active pin viewer modal */}
      {activePin && (
        <div
          className="fixed inset-0 z-[70] bg-emerald-deep/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActivePin(null)}
          data-testid="memory-viewer-modal"
        >
          <div
            className="bg-ivory rounded-2xl max-w-md w-full p-7 relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="memory-viewer-close"
              onClick={() => setActivePin(null)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-saffron text-emerald-deep border-2 border-emerald-deep flex items-center justify-center font-bold">
                {activePin.name.split(" ")[0]?.charAt(0) || "•"}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Classmate</div>
                <div className="font-display text-2xl text-emerald-deep">{activePin.name}</div>
              </div>
            </div>
            {activePin.note && (
              <p className="mt-5 text-stone-700 leading-relaxed font-serif-accent italic">
                "{activePin.note}"
              </p>
            )}
            {isAdmin && (
              <div className="mt-6 pt-4 border-t border-stone-200 flex justify-end">
                <button
                  data-testid={`memory-viewer-delete-${activePin.id}`}
                  onClick={() => removePin(activePin.id)}
                  className="text-stone-500 hover:text-red-600 text-sm flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove this memory
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin add-pin form modal */}
      {pendingPos && (
        <div
          className="fixed inset-0 z-[70] bg-emerald-deep/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={cancelAdd}
          data-testid="memory-add-modal"
        >
          <div
            className="bg-ivory rounded-2xl max-w-md w-full p-7 relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="memory-add-close"
              onClick={cancelAdd}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">New memory</div>
            <div className="font-display text-2xl text-emerald-deep mt-1">Identify this classmate</div>
            <p className="text-stone-600 text-sm mt-1">
              Position {pendingPos.x.toFixed(1)}%, {pendingPos.y.toFixed(1)}% — anyone you recognize?
            </p>

            <div className="mt-5 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-600">Name</label>
                <Input
                  data-testid="memory-add-name"
                  autoFocus
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Sri. Ramesh"
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-600">Memory (optional)</label>
                <Textarea
                  data-testid="memory-add-note"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="A line or two — sat next to me, captain of the football team…"
                  className="mt-1.5 min-h-[100px]"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  data-testid="memory-add-save"
                  onClick={submitPin}
                  disabled={saving}
                  className="flex-1 bg-emerald-deep hover:bg-navy text-ivory h-11"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  {saving ? "Saving…" : "Save memory"}
                </Button>
                <Button
                  data-testid="memory-add-cancel"
                  onClick={cancelAdd}
                  variant="outline"
                  className="h-11"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </figure>
  );
}
